import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  FileText,
  Headphones,
  Map,
  Quote,
  Star,
  Target,
  UtensilsCrossed,
  Video,
} from "lucide-react";

export type ContentTypeKey =
  | "audio"
  | "video"
  | "blog"
  | "recipe"
  | "affirmation"
  | "workbook"
  | "horoscope"
  | "journey"
  | "challenge";

export interface ContentTypeMeta {
  key: ContentTypeKey;
  label: string;
  icon: LucideIcon;
  apiBase: string;
  studioListUrl: string;
  studioEditUrl: (id: string) => string;
}

export const CONTENT_TYPES: Record<ContentTypeKey, ContentTypeMeta> = {
  audio: {
    key: "audio",
    label: "Audio",
    icon: Headphones,
    apiBase: "/audio",
    studioListUrl: "/studio/audio",
    studioEditUrl: (id) => `/studio/audio/${id}/edit`,
  },
  video: {
    key: "video",
    label: "Video",
    icon: Video,
    apiBase: "/video",
    studioListUrl: "/studio/video",
    studioEditUrl: (id) => `/studio/video/${id}/edit`,
  },
  blog: {
    key: "blog",
    label: "Blog",
    icon: FileText,
    apiBase: "/content-blogs",
    studioListUrl: "/studio/blog",
    studioEditUrl: (id) => `/studio/blog/${id}/edit`,
  },
  recipe: {
    key: "recipe",
    label: "Recipe",
    icon: UtensilsCrossed,
    apiBase: "/content-recipes",
    studioListUrl: "/studio/recipes",
    studioEditUrl: (id) => `/studio/recipes/${id}/edit`,
  },
  affirmation: {
    key: "affirmation",
    label: "Affirmation",
    icon: Quote,
    apiBase: "/content-affirmations",
    studioListUrl: "/studio/affirmations",
    studioEditUrl: (id) => `/studio/affirmations/${id}/edit`,
  },
  workbook: {
    key: "workbook",
    label: "Workbook",
    icon: BookOpen,
    apiBase: "/content-workbooks",
    studioListUrl: "/studio/workbooks",
    studioEditUrl: (id) => `/studio/workbooks/${id}/edit`,
  },
  horoscope: {
    key: "horoscope",
    label: "Horoscope",
    icon: Star,
    apiBase: "/horoscope/entries",
    studioListUrl: "/studio/horoscope",
    studioEditUrl: (id) => `/studio/astrology/${id}/edit`,
  },
  journey: {
    key: "journey",
    label: "Journey",
    icon: Map,
    apiBase: "/content-journeys",
    studioListUrl: "/studio/journeys",
    studioEditUrl: (id) => `/studio/journeys/${id}/edit`,
  },
  challenge: {
    key: "challenge",
    label: "Challenge",
    icon: Target,
    apiBase: "/content-challenges",
    studioListUrl: "/studio/challenges",
    studioEditUrl: (id) => `/studio/challenges/${id}/edit`,
  },
};

const FALLBACK_META: ContentTypeMeta = {
  key: "blog" as ContentTypeKey,
  label: "Content",
  icon: FileText,
  apiBase: "",
  studioListUrl: "/studio",
  studioEditUrl: () => "/studio",
};

export function getContentTypeMeta(type: string): ContentTypeMeta {
  return (
    CONTENT_TYPES[type as ContentTypeKey] ?? {
      ...FALLBACK_META,
      label: type || "Content",
    }
  );
}

export function getContentTypeApiBase(type: string): string | null {
  const meta = CONTENT_TYPES[type as ContentTypeKey];
  return meta ? meta.apiBase : null;
}

export const CONTENT_TYPE_KEYS: ContentTypeKey[] = Object.keys(
  CONTENT_TYPES,
) as ContentTypeKey[];

export const CONTENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(CONTENT_TYPES).map(([k, v]) => [k, v.label]),
);

export const CONTENT_TYPE_ICONS: Record<string, LucideIcon> =
  Object.fromEntries(
    Object.entries(CONTENT_TYPES).map(([k, v]) => [k, v.icon]),
  );
